import { Controller, type UseFormReturn } from 'react-hook-form'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import type { ExerciseFormValues, ExerciseTaxonomies, ValidationReport } from '@entities/exercise/model'
import { ExerciseMediaPicker } from './media-picker'
import { MusclePicker } from './muscle-picker'

const numberValue = (value: string) => (value === '' ? undefined : Number(value))

export const ExerciseEditorFields = ({ form, taxonomies, editable, validation }: {
	form: UseFormReturn<ExerciseFormValues>
	taxonomies: ExerciseTaxonomies
	editable: boolean
	validation: ValidationReport | null
}) => {
	const { register, control } = form
	return (
		<div className='space-y-8'>
			<section className='space-y-4'>
				<h3 className='text-lg font-semibold'>Основная информация</h3>
				<div className='grid gap-4 md:grid-cols-2'>
					<div className='md:col-span-2'>
						<Label htmlFor='exercise-name'>Название</Label>
						<Input id='exercise-name' {...register('name', { required: true, maxLength: 180 })} disabled={!editable} className='mt-2' />
					</div>
					<label className='md:col-span-2 text-sm font-medium'>Описание
						<textarea {...register('description')} disabled={!editable} rows={4} className='mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm' />
					</label>
					<label className='md:col-span-2 text-sm font-medium'>Техника выполнения
						<textarea {...register('technique')} disabled={!editable} rows={5} className='mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm' />
					</label>
					<label className='text-sm font-medium'>Тип
						<select {...register('exercise_type', { required: true })} disabled={!editable} className='mt-2 h-10 w-full rounded-md border bg-background px-3 text-sm'>
							{taxonomies.types.filter(item => item.active).map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
						</select>
					</label>
					<label className='text-sm font-medium'>Сложность
						<select {...register('difficulty_code')} disabled={!editable} className='mt-2 h-10 w-full rounded-md border bg-background px-3 text-sm'>
							<option value=''>Не выбрана</option>
							{taxonomies.difficulties.filter(item => item.active).map(item => <option key={item.code} value={item.code}>{item.name}</option>)}
						</select>
					</label>
				</div>
			</section>

			<section className='space-y-4'>
				<h3 className='text-lg font-semibold'>Параметры и оборудование</h3>
				<div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
					{([['default_sets', 'Подходы'], ['default_reps', 'Повторения'], ['default_duration', 'Длительность, сек.'], ['default_rest_time', 'Отдых, сек.']] as const).map(([field, label]) => (
						<label key={field} className='text-sm font-medium'>{label}
							<Input type='number' min={0} disabled={!editable} className='mt-2' {...register(field, { setValueAs: numberValue })} />
						</label>
					))}
				</div>
				<div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-3'>
					{taxonomies.equipment.filter(item => item.active).map(item => (
						<label key={item.code} className='flex items-center gap-3 rounded-lg border p-3 text-sm'>
							<input type='checkbox' value={item.code} disabled={!editable} {...register('equipment_codes')} className='h-4 w-4 accent-primary' />
							<span>{item.name}</span>
						</label>
					))}
				</div>
				<div className='grid gap-3 sm:grid-cols-2'>
					<Controller control={control} name='is_weight' render={({ field }) => (
						<label className='flex items-center justify-between rounded-lg border p-4 text-sm font-medium'><span>Упражнение с весом</span><Switch checked={field.value} onCheckedChange={field.onChange} disabled={!editable} /></label>
					)} />
					<Controller control={control} name='is_ai_available' render={({ field }) => (
						<label className='flex items-center justify-between rounded-lg border p-4 text-sm font-medium'><span>Доступно для AI-функций</span><Switch checked={field.value} onCheckedChange={field.onChange} disabled={!editable} /></label>
					)} />
				</div>
			</section>

			<section className='space-y-4'>
				<h3 className='text-lg font-semibold'>Мышцы</h3>
				<Controller control={control} name='muscles' render={({ field }) => (
					<MusclePicker muscles={taxonomies.muscles} value={field.value} onChange={field.onChange} disabled={!editable} />
				)} />
			</section>

			<section className='space-y-4'>
				<div>
					<h3 className='text-lg font-semibold'>Медиа</h3>
					<p className='mt-1 text-sm text-muted-foreground'>Выбираются только готовые обработанные медиафайлы. Отсоединение не удаляет файл из медиатеки.</p>
				</div>
				<Controller control={control} name='media' render={({ field }) => (
					<ExerciseMediaPicker value={field.value} onChange={field.onChange} disabled={!editable} />
				)} />
			</section>

			<section className='space-y-4'>
				<h3 className='text-lg font-semibold'>Безопасность</h3>
				<div className='grid gap-4 md:grid-cols-2'>
					<label className='text-sm font-medium'>Примечания по безопасности<textarea {...register('safety_notes')} disabled={!editable} rows={4} className='mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm' /></label>
					<label className='text-sm font-medium'>Противопоказания<textarea {...register('contraindications')} disabled={!editable} rows={4} className='mt-2 w-full rounded-md border bg-background px-3 py-2 text-sm' /></label>
				</div>
			</section>

			{validation ? (
				<section className={`rounded-xl border p-4 ${validation.valid ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
					<h3 className='font-semibold'>{validation.valid ? 'Публикационная проверка пройдена' : 'Результаты проверки'}</h3>
					{validation.issues.length ? <ul className='mt-3 space-y-2 text-sm'>{validation.issues.map((issue, index) => <li key={`${issue.code}-${index}`}><strong>{issue.severity}</strong> {issue.path ? `${issue.path}: ` : ''}{issue.message}</li>)}</ul> : null}
				</section>
			) : null}
		</div>
	)
}
